import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { StripeEnv } from '../../../util/enums';
import useOrgData from './useOrgData';
import useFinancialAccountData from './useFinancialAccountData';
import useKyb from './useKyb';
import usePrimaryKyc from './usePrimaryKyc';
import usePayableOrg from './usePayableOrg';
import useFinancialAccountRoots from './useFinancialAccountRoots';
import useAccountVerification from './useAccountVerification';
import logger from '../../../util/logger';
import { formatOrgData } from '../services/formatOrgData';
import useUserKyc from './useUserKyc';
import useOwnersData from './useOwnersData';

export type OrgsDataForForm = {
  kyb?: Kyb;
  primaryKyc?: Kyc;
  primaryRepresentativeStripePersonId?: string;
  primaryRepresentativeUserId?: string;
  submittedOnYourBehalf?: boolean;
  org?: OrgNode;
  payableOrg?: OrgNode;
  financialAccountNode?: FinancialAccountNode;
  financialAccountRoots?: FinancialAccountNode[];
  errorMessage?: string;
  isFinished: boolean;
  replaceRepresentative: boolean;
  userKyc?: Kyc;
  owners: Owner[];
};

export default function useOrgsData({
  orgId,
  awaitOrgId,
  payableOrgId,
  provider,
  stripeEnv,
  currentUserId,
  awaitCurrentUserId,
  awaitPayableOrgId,
  refreshKyb,
}: {
  orgId?: string;
  awaitOrgId: boolean;
  payableOrgId?: string;
  provider: VaultProvider;
  stripeEnv: StripeEnv;
  currentUserId?: string;
  awaitCurrentUserId: boolean;
  awaitPayableOrgId: boolean;
  refreshKyb: Symbol;
}): OrgsDataForForm {
  const {
    org,
    errorMessage: orgErrorMessage,
    isFinished: orgDataIsFinished,
  } = useOrgData({
    awaitOrgId,
    orgId,
    refreshKyb,
  });

  const {
    userKyc,
    errorMessage: userKycErrorMessage,
    isFinished: userKycIsFinished,
  } = useUserKyc({ currentUserId, awaitCurrentUserId });

  const {
    financialAccountRoots,
    errorMessage: financialAccountRootsErrorMessage,
    isFinished: financialAccountRootsAreFinished,
  } = useFinancialAccountRoots({ org, orgDataIsFinished });

  const {
    financialAccountNode,
    submittedOnYourBehalf,
    errorMessage: financialAccountErrorMessage,
    isFinished: financialAccountIsFinished,
  } = useFinancialAccountData({
    org,
    orgDataIsFinished,
    financialAccountRoots,
    financialAccountRootsAreFinished,
    provider,
    stripeEnv,
    payableOrgId,
  });

  const {
    kyb,
    errorMessage: kybErrorMessage,
    isFinished: kybIsFinished,
  } = useKyb({
    financialAccountNode,
    financialAccountIsFinished,
    refreshKyb,
  });

  const {
    primaryKyc,
    errorMessage: primaryKycErrorMessage,
    isFinished: primaryKycIsFinished,
    representativeUserId: primaryRepresentativeUserId,
    representativeStripePersonId: primaryRepresentativeStripePersonId,
    replaceRepresentative,
  } = usePrimaryKyc({
    org,
    orgDataIsFinished,
    financialAccountNode,
    financialAccountIsFinished,
    provider,
    stripeEnv,
    currentUserId,
    awaitCurrentUserId,
    financialAccountRoots,
    financialAccountRootsAreFinished,
    payableOrgId,
  });

  const {
    payableOrg,
    errorMessage: payableOrgErrorMessage,
    isFinished: payableOrgIsFinished,
  } = usePayableOrg({
    payableOrgId,
    awaitPayableOrgId,
  });

  const {
    errorMessage: accountVerificationErrorMessage,
    isFinished: accountVerificationIsFinished,
  } = useAccountVerification({
    financialAccountNode,
    financialAccountIsFinished,
  });

  const { owners, isFinished: ownersAreFinished } = useOwnersData({
    kyb,
    orgId,
    kybIsFinished,
    orgDataIsFinished,
    primaryKycIsFinished,
    submittedOnYourBehalf: !!submittedOnYourBehalf,
    payableOrgId,
  });

  const errorMessage =
    orgErrorMessage ||
    financialAccountRootsErrorMessage ||
    financialAccountErrorMessage ||
    kybErrorMessage ||
    primaryKycErrorMessage ||
    payableOrgErrorMessage ||
    accountVerificationErrorMessage ||
    userKycErrorMessage;

  logger(`userKyc is finished: ${userKycIsFinished}`);
  logger(`orgData is finished: ${orgDataIsFinished}`);
  logger(
    `financialAccountRoots are finished: ${financialAccountRootsAreFinished}`,
  );
  logger(`financialAccount is finished: ${financialAccountIsFinished}`);
  logger(`kyb is finished: ${kybIsFinished}`);
  logger(`primaryKyc is finished: ${primaryKycIsFinished}`);
  logger(`payableOrg is finished: ${payableOrgIsFinished}`);
  logger(`accountVerification is finished: ${accountVerificationIsFinished}`);
  logger(`owners are finished: ${ownersAreFinished}`);

  const isFinished =
    orgDataIsFinished &&
    financialAccountRootsAreFinished &&
    financialAccountIsFinished &&
    kybIsFinished &&
    primaryKycIsFinished &&
    payableOrgIsFinished &&
    accountVerificationIsFinished &&
    userKycIsFinished &&
    ownersAreFinished;

  logger(`orgsData is finished: ${isFinished}`);

  let formattedOrg: OrgNode | undefined = undefined;
  if (org !== undefined && isFinished) {
    if (payableOrg !== undefined) {
      formattedOrg = formatOrgData(org, payableOrg);
    } else if (!awaitPayableOrgId) {
      formattedOrg = org;
    }
  }

  return {
    kyb,
    primaryKyc,
    submittedOnYourBehalf,
    org: formattedOrg,
    payableOrg,
    financialAccountNode,
    financialAccountRoots,
    errorMessage,
    isFinished,
    primaryRepresentativeUserId,
    primaryRepresentativeStripePersonId,
    replaceRepresentative,
    userKyc,
    owners,
  };
}
